import { DataType as formulaType } from '@mode-switch/express';
import { DataType, VariableType } from '@mode-vegas/vegas';
import { StateRecord } from '../common.types';

export const enum ReportFilterType {
  Single = 'SINGLE',
  Multi = 'MULTI',
  Range = 'RANGE',
}

export const enum ReportFilterControlType {
  List = 'LIST',
  Dropdown = 'DROPDOWN',
  Range = 'RANGE',
}

export const enum DiscreteSelectionType {
  All,
  None,
  Select,
  Exclude,
  Include,
  ExcludeAll,
}

export type SelectControlType = ReportFilterControlType.List | ReportFilterControlType.Dropdown;

export type RangeControlType = ReportFilterControlType.Range;

export type ReportRangeFilterValue = number;

export type ReportFilterValue = string | boolean | ReportRangeFilterValue | null;

export interface ReportFilterRange {
  readonly min: ReportRangeFilterValue | null;
  readonly max: ReportRangeFilterValue | null;
}

export interface IReportFilterResource extends StateRecord {
  token: string;
  name: string;
  row_order: number;
  formula: string;
  data_type: DataType;
  formula_type: DataType;
  variable_type: VariableType;
  filter_type: ReportFilterType;
  hide_select_all: boolean;
  show_relevant_values_only: boolean;

  _links: any[];
  _forms: any[];

  forms: (name: string) => any;
}

export interface SingleReportFilterResource extends IReportFilterResource {
  filter_type: ReportFilterType.Single;
  control_type: SelectControlType;
  options: {
    default: {
      all: boolean;
      value: ReportFilterValue | undefined;
    };
  };
}

export interface MultiReportFilterResource extends IReportFilterResource {
  filter_type: ReportFilterType.Multi;
  control_type: SelectControlType;
  options: {
    default: {
      all: boolean;
      values: ReportFilterValue[];
    };
  };
}

export interface RangeReportFilterResource extends IReportFilterResource {
  filter_type: ReportFilterType.Range;
  control_type: RangeControlType;
  options: {
    default: ReportFilterRange;
  };
}

export type ReportFilterResource = SingleReportFilterResource | MultiReportFilterResource | RangeReportFilterResource;

export interface FilterDefinition {
  token: string;
  name: string;
  position: number;
  formula: string;
  dataType: DataType;
  formulaType: DataType;
  varType: VariableType;
  filterType: ReportFilterType;
  controlType: SelectControlType | RangeControlType;
  hideSelectAll: boolean;
  showRelevantValuesOnly: boolean;
}

export interface SingleValueFilterDefinition extends FilterDefinition {
  filterType: ReportFilterType.Single;
  controlType: SelectControlType;
}

export interface MultiValueFilterDefinition extends FilterDefinition {
  filterType: ReportFilterType.Multi;
  controlType: SelectControlType;
}

export interface RangeValueFilterDefinition extends FilterDefinition {
  filterType: ReportFilterType.Range;
  controlType: RangeControlType;
}

export type ReportFilterDefinition =
  | SingleValueFilterDefinition
  | MultiValueFilterDefinition
  | RangeValueFilterDefinition;

// ...Control interfaces are for maintaining a similar structure to the original report filter code
export interface SingleValueFilterControl extends SingleValueFilterDefinition {
  filterType: ReportFilterType.Single;
  controlType: SelectControlType;

  selected: SingleValueSelection;
  default: SingleValueSelection;
}

export interface MultiValueFilterControl extends MultiValueFilterDefinition {
  default: MultiValueSelection;
  selected: MultiValueSelection;
}

export interface RangeValueFilterControl extends RangeValueFilterDefinition {
  selected: ReportFilterRange;
  default: ReportFilterRange;
}

export type ReportFilterControl = SingleValueFilterControl | MultiValueFilterControl | RangeValueFilterControl;

export interface MultiValueSelection {
  all: boolean;
  values: ReadonlyArray<ReportFilterValue>;
}

export interface SingleValueSelection {
  all: boolean;
  value: ReportFilterValue | undefined;
}

export type FilterSelection = MultiValueSelection | SingleValueSelection | ReportFilterRange;
export type FilterValueSelection = MultiValueSelection | SingleValueSelection;

export interface DiscreteValueOption {
  value: ReportFilterValue;
  label: string;
}

export interface SingleSelectFilterOptions {
  readonly filterType: ReportFilterType.Single;
  readonly values: DiscreteValueOption[];
}

export interface MultiSelectFilterOptions {
  readonly filterType: ReportFilterType.Multi;
  readonly values: DiscreteValueOption[];
}

export interface RangeSelectFilterOptions {
  readonly filterType: ReportFilterType.Range;
  readonly values: ReportFilterRange;
}

export type ReportFilterOptions = SingleSelectFilterOptions | MultiSelectFilterOptions | RangeSelectFilterOptions;

/**
 * Below are types for the report filter creation UI
 */
export enum ReportFilterFieldType {
  Field = 'FIELD',
  Folder = 'FOLDER',
}

export interface ReportFilterField {
  readonly id: string;
  readonly type: ReportFilterFieldType.Field;
  readonly name: string;
  readonly control: FilterDefinition;
  readonly inUse: boolean;
  readonly isAggregate: boolean;
  readonly isCalc: boolean;
  readonly formulaType: formulaType.Name;
}

export interface ReportFilterFieldFolder {
  readonly id: string;
  readonly type: ReportFilterFieldType.Folder;
  readonly name: string;
  readonly fields: ReportFilterField[];
  readonly isCalc: boolean;
  readonly formulaType: formulaType.Name;
}

export type ReportFilterFieldTypes = ReportFilterField | ReportFilterFieldFolder;

export interface DiscreteFilterSelection {
  type: DiscreteSelectionType.Select;
  value: ReportFilterValue;
  selected: boolean;
}

export interface DiscreteFilterAllSelection {
  type:
    | DiscreteSelectionType.All
    | DiscreteSelectionType.None
    | DiscreteSelectionType.Exclude
    | DiscreteSelectionType.ExcludeAll
    | DiscreteSelectionType.Include;
}

export type DiscreteFilterSelectionTypes = DiscreteFilterSelection | DiscreteFilterAllSelection;
